import * as React from 'react';
import { cn } from '@/lib/utils';

export interface SegmentedControlOption<T extends string = string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
}

export interface SegmentedControlProps<T extends string = string> {
  options: SegmentedControlOption<T>[];
  value: T;
  onValueChange: (value: T) => void;
  'aria-label': string;
  className?: string;
}

export function SegmentedControl<T extends string = string>({
  options,
  value,
  onValueChange,
  'aria-label': ariaLabel,
  className,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        // Shell: accent track with visible center bar (this was the \"close\" version you liked)
        'flex w-full items-stretch gap-[3px] rounded-xl border border-accent bg-accent p-1',
        'transition-shadow duration-200 focus-within:ring-2 focus-within:ring-ring/60 focus-within:ring-offset-2 focus-within:ring-offset-background',
        className
      )}
    >
      {options.map((option, index) => {
        const isSelected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onValueChange(option.value)}
            className={cn(
              'flex flex-1 items-center justify-center gap-2 py-4 text-sm font-medium transition-all duration-200',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              // Left option: rounded left side only; right option: rounded right side only
              index === 0 ? 'rounded-l-lg' : 'rounded-r-lg',
              isSelected
                ? 'bg-primary text-primary-foreground shadow-[0_2px_4px_rgba(99,102,241,0.3)]'
                : 'bg-card text-foreground/45 hover:bg-[#263349] hover:text-foreground/70'
            )}
          >
            {option.icon != null && (
              <span aria-hidden className="text-base leading-none">{option.icon}</span>
            )}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
